import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Plus } from 'lucide-react'
import type { ColumnDef, Row } from '@tanstack/react-table'
import { DataTable } from '@/shared/components/data-table/data-table'
import { Button } from '@/shared/components/ui/button'
import { usePermissions } from '@/shared/lib/permissions/use-permissions'
import { PermissionLevel } from '@/shared/lib/permissions/permission-values'
import { useSetPageMeta } from '@/shared/lib/context/page-meta-context'
import type { VisibilityState } from '@tanstack/react-table'
import type { ServerTableState } from '@/shared/hooks/use-server-table'

interface ListPageProps<TData> {
  title: string
  description?: string
  columns: ColumnDef<TData>[]
  data: TData[]
  totalRows: number
  isLoading: boolean
  tableState: ServerTableState
  /**
   * Legacy localStorage key for uncontrolled column visibility persistence.
   * Optional and ignored when `columnVisibility` is controlled.
   */
  storageKey?: string
  /** Permission module name to gate create/delete actions */
  permissionName?: string
  createLabel?: string
  /**
   * Emphasis of the create action. Ghost by default so the toolbar of a plain list stays flat;
   * a screen whose create action competes with other entries passes "default" to make it the
   * one primary action.
   */
  createVariant?: 'ghost' | 'default'
  onCreateClick?: () => void
  onExport?: () => void
  /** Extra content rendered in the header action area */
  headerActions?: React.ReactNode
  /** Default column visibility applied when no state is provided */
  defaultColumnVisibility?: VisibilityState
  /** Controlled column visibility. Opt-in for the persisted view feature. */
  columnVisibility?: VisibilityState
  onColumnVisibilityChange?: (visibility: VisibilityState) => void
  /** Shows the "Reset view" button and "Saved view" pill when true. */
  hasSavedView?: boolean
  onResetView?: () => void
  /** When provided, switches pagination to "unknown total" mode. true = has more pages. */
  hasMore?: boolean
  /** Mark this page as demo — shows the demo tenant banner */
  demo?: boolean
  /** Forwarded to DataTable: invoked when a row is clicked. */
  onRowClick?: (row: TData) => void
  /** Forwarded to DataTable: renders an inline expandable row with detail content. */
  renderSubRow?: (row: Row<TData>) => React.ReactNode
  /** Forwarded to DataTable: whether a given row can expand. */
  getRowCanExpand?: (row: Row<TData>) => boolean
  /** Forwarded to DataTable: a row click toggles its expansion. */
  expandOnRowClick?: boolean
  /** Optional breadcrumb back link rendered in the topbar. */
  back?: { label: string; to: string }
  /** Optional discreet notice rendered above the table (e.g. partial-data banners). */
  banner?: React.ReactNode
  /** Forwarded to DataTable: what an empty list says instead of «No results.». */
  emptyState?: React.ReactNode
  /** Forwarded to DataTable: classes for a row, replacing its zebra stripe (e.g. a status colour). */
  getRowClassName?: (row: TData) => string | undefined
}

export function ListPage<TData>({
  title,
  description,
  columns,
  data,
  totalRows,
  isLoading,
  tableState,
  storageKey,
  permissionName,
  createLabel,
  createVariant = 'ghost',
  onCreateClick,
  onExport,
  headerActions,
  defaultColumnVisibility,
  columnVisibility,
  onColumnVisibilityChange,
  hasSavedView,
  onResetView,
  hasMore,
  demo,
  onRowClick,
  renderSubRow,
  getRowCanExpand,
  expandOnRowClick,
  back,
  banner,
  emptyState,
  getRowClassName,
}: ListPageProps<TData>) {
  const { t } = useTranslation()
  const { can } = usePermissions()
  const resolvedCreateLabel = createLabel ?? t('dataTable.new', 'New')
  const canCreate =
    !permissionName || can({ name: permissionName, level: PermissionLevel.CreateEdit })

  const actions = useMemo(
    () => (
      <div className="flex items-center gap-2">
        {headerActions}
        {onCreateClick && canCreate && (
          <Button
            variant={createVariant}
            size="sm"
            className={createVariant === 'default'
              ? 'h-7 gap-1.5 px-2.5'
              : 'h-7 gap-1.5 px-2 text-toolbar-foreground/70 hover:text-toolbar-foreground hover:bg-toolbar-foreground/5'}
            onClick={onCreateClick}
          >
            <Plus className="h-3.5 w-3.5" />
            <span className="hidden lg:inline text-xs">{resolvedCreateLabel}</span>
          </Button>
        )}
      </div>
    ),
    [headerActions, onCreateClick, canCreate, resolvedCreateLabel, createVariant],
  )

  useSetPageMeta({ title, description, actions, demo, back })

  return (
    <div className="flex flex-col gap-4 p-6 h-full min-h-0">
      {banner}
      {/* Table */}
      <div className="flex-1 min-h-0">
        <DataTable
          columns={columns}
          data={data}
          totalRows={totalRows}
          hasMore={hasMore}
          isLoading={isLoading}
          emptyState={emptyState}
          storageKey={storageKey}
          onExport={onExport}
          defaultColumnVisibility={defaultColumnVisibility}
          columnVisibility={columnVisibility}
          onColumnVisibilityChange={onColumnVisibilityChange}
          hasSavedView={hasSavedView}
          onResetView={onResetView}
          onRowClick={onRowClick}
          renderSubRow={renderSubRow}
          getRowCanExpand={getRowCanExpand}
          expandOnRowClick={expandOnRowClick}
          getRowClassName={getRowClassName}
          {...tableState}
        />
      </div>
    </div>
  )
}
